import { Reducer } from 'redux';

import { removeWallet } from './walletReducer';
import { WalletType } from './walletUtils';

// Actions

export const changeWalletChainId = (chainId?: number) => ({
  type: 'CHANGE_WALLET_CHAIN_ID' as const,
  chainId,
});

// TODO: this should only be displayed on signature request ? TRY to change this functionality...
export const updatePendingWalletAddress = (pendingWalletAddress?: string) => ({
  type: 'UPDATE_PENDING_WALLET_ADDRESS' as const,
  pendingWalletAddress,
});

export const showWalletModal = ({
  show,
  afterGoTo,
  preSelect,
  keepWalletUntilSuccess,
}: {
  show?: boolean;
  afterGoTo?: string;
  preSelect?: {
    walletType: WalletType;
    rdns?: string;
    remember: boolean;
  };
  keepWalletUntilSuccess?: boolean;
} = {}) => ({
  type: 'SHOW_WALLET_DETAIL_MODAL' as const,
  show: show ?? true,
  afterGoTo,
  preSelect,
  keepWalletUntilSuccess,
});

export const showWalletNonDelegatedModal = () => ({
  type: 'SHOW_WALLET_NON_DELEGATED_DETAIL_MODAL' as const,
  show: true,
  afterGoTo: undefined,
});

export const showDesktopQRConnectModal = (
  modal: WalletDetailState['desktopQrConnectModal']
) => ({
  type: 'SHOW_DEKSTOP_QR_CONNECT' as const,
  modal,
});

// unlockWalletConnectQRUrl
export const showWalletConnectQR = (url?: string) => ({
  type: 'SHOW_WALLET_CONNECT_QR' as const,
  url,
});

type WalletDetailAction = ReturnType<
  | typeof removeWallet
  | typeof changeWalletChainId
  | typeof updatePendingWalletAddress
  | typeof showWalletModal
  | typeof showWalletNonDelegatedModal
  | typeof showDesktopQRConnectModal
  | typeof showWalletConnectQR
>;

// Reducer
type WalletDetailState = {
  /** Desktop Unlock modal */
  unlockModalOpen: boolean;
  unlockModalAfterGoTo?: string;
  unlockModalDelegationDisabled: boolean;
  unlockModalKeepWalletUntilSuccess: boolean;

  unlockWalletConnectQRUrl?: string;

  walletChainId?: number;
  pendingWalletAddress?: string;

  preSelectWallet?: { type: WalletType; rdns?: string };
  preSelectRemember?: boolean;

  /** IDEX QR Code connect. Desktop -> mobile */
  desktopQrConnectModal: false | 'standard' | 'withoutDoNotShowAgain';
};

const defaultState: WalletDetailState = {
  unlockModalOpen: false,
  unlockModalAfterGoTo: undefined,
  unlockModalDelegationDisabled: false,
  unlockModalKeepWalletUntilSuccess: false,
  walletChainId: undefined,
  pendingWalletAddress: undefined,

  desktopQrConnectModal: false as const,
};

/**
 * Purpose of this reducer is to track last known chain id for software wallets,
 * so we can notify user about not correctly configured chain id.
 * Note: It is not used to restrict sign activities
 */
export const walletDetailReducer: Reducer<
  WalletDetailState,
  WalletDetailAction
> = (state = defaultState, action): WalletDetailState => {
  switch (action.type) {
    case 'REMOVE_WALLET':
      return {
        ...defaultState,
        unlockModalOpen: state.unlockModalOpen,
        unlockModalAfterGoTo: state.unlockModalAfterGoTo,
        unlockModalDelegationDisabled: state.unlockModalDelegationDisabled,
        unlockModalKeepWalletUntilSuccess:
          state.unlockModalKeepWalletUntilSuccess,
      };
    case 'SHOW_WALLET_DETAIL_MODAL':
      return {
        ...state,
        unlockModalOpen: action.show,
        unlockModalAfterGoTo: action.afterGoTo,
        unlockModalDelegationDisabled: false,
        unlockModalKeepWalletUntilSuccess: !!action.keepWalletUntilSuccess,
        preSelectWallet: action.preSelect
          ? {
              type: action.preSelect?.walletType,
              rdns: action.preSelect?.rdns,
            }
          : undefined,
        preSelectRemember: action.preSelect?.remember,
      };
    case 'SHOW_WALLET_NON_DELEGATED_DETAIL_MODAL':
      return {
        ...state,
        unlockModalOpen: action.show,
        unlockModalAfterGoTo: action.afterGoTo,
        unlockModalDelegationDisabled: true,
        unlockModalKeepWalletUntilSuccess: false,
      };
    case 'CHANGE_WALLET_CHAIN_ID':
      return {
        ...state,
        walletChainId: action.chainId ? Number(action.chainId) : action.chainId,
      };
    case 'UPDATE_PENDING_WALLET_ADDRESS':
      return {
        ...state,
        pendingWalletAddress: action.pendingWalletAddress?.toLowerCase(),
      };
    case 'SHOW_DEKSTOP_QR_CONNECT':
      return {
        ...state,
        desktopQrConnectModal: action.modal,
      };
    case 'SHOW_WALLET_CONNECT_QR':
      return {
        ...state,
        unlockWalletConnectQRUrl: action.url,
      };
    default:
      return state;
  }
};
